import { Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PortalLink } from '~/components/PortalLink';
import { AppearanceIncoming, defaultAppearance } from '~/data/appearance';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { AIChatbotButton } from '~/legacy-components/Topbar/components/AIChatbotButton';
import { TopbarSurvey } from '~/legacy-components/TopbarSurvey';
import { legacyColors } from '~/legacyTheme';
import { isMobileBrowser } from '~/utils/helper';

import { LegacyNotificationsPaper } from '../LegacyNotificationsPaper';
import { LegacyUserProfile } from '../LegacyUserProfile';

import { CompanyLogo } from './components/CompanyLogo';
import { useSxStyles } from './useStyles';
import { useTopbar } from './useTopbar';

export const Topbar = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const {
    companyData,
    employeeId,
    loggedInEmployeeId,
    canAdminister,
    canAddEmployee,
    canAddEmployeeCandidate,
    newEmployeePage,
    isAIChatbotEnabled,
  } = useTopbar();

  // TODO Re-enable Topbar customAppearance & topBarColorSet when decided by management,
  //  use fixed customAppearance styling until then.
  // const { customAppearance } = useAppearance();
  const customAppearance: AppearanceIncoming = {
    ...defaultAppearance,
    fTopBarColors: {
      fIconsColor: legacyColors.white1,
      fBackgroundColor: legacyColors.blue2,
    },
  };
  const topBarColorSet = false;
  // customAppearance.fTopBarColors &&
  // customAppearance.fTopBarColors.fBackgroundColor !== '';

  const itemColors = topBarColorSet
    ? {
        color: customAppearance.fTopBarColors.fIconsColor,
        backgroundColor: customAppearance.fTopBarColors.fBackgroundColor,
        '&:hover': {
          backgroundColor: `${customAppearance.fTopBarColors.fIconsColor}40`,
        },
      }
    : {
        color: legacyColors.blue2,
        '&:hover': {
          color: legacyColors.white,
          backgroundColor: legacyColors.blue3,
        },
      };

  const navItemSxStyle = { ...itemColors, ...sxStyles.navItem };

  return (
    <Box
      sx={{
        ...sxStyles.container,
        backgroundColor: topBarColorSet
          ? customAppearance.fTopBarColors.fBackgroundColor
          : legacyColors.blue,
      }}
    >
      <PortalLink sx={sxStyles.logoContainer} to={'/portal'}>
        <CompanyLogo
          companyLogoData={companyData?.logo}
          sx={{ color: legacyColors.white }}
        />
      </PortalLink>
      <Box sx={sxStyles.rightContainer}>
        <Box component="nav" style={sxStyles.navContainer}>
          <TopbarSurvey style={itemColors} />
          {isMobileBrowser() && (
            <PortalLink to={'/mobile'} sx={navItemSxStyle}>
              <LegacyFAIcon FAIcon={LegacyFAIcons.Mobile} />
            </PortalLink>
          )}
          {isAIChatbotEnabled && <AIChatbotButton sx={navItemSxStyle} />}
          <Link
            sx={navItemSxStyle}
            href={`/page-tutorial.html?emp=${employeeId}`}
            title={t('laVideoTutorials')}
          >
            <LegacyFAIcon FAIcon={LegacyFAIcons.VideoCamera} />
          </Link>
          {(canAddEmployee || canAddEmployeeCandidate) && (
            <Link
              sx={navItemSxStyle}
              href={
                canAddEmployee
                  ? `/${newEmployeePage}?emp=${employeeId}`
                  : `/page-newEmpManager.html?emp=${employeeId}`
              }
              title={canAddEmployee ? t('laNewEmployee') : t('laNewCandidate')}
            >
              <LegacyFAIcon FAIcon={LegacyFAIcons.UserPlus} />
            </Link>
          )}
          {canAdminister && (
            <Link
              sx={navItemSxStyle}
              href={`/page-admin.html?emp=${employeeId}`}
            >
              <LegacyFAIcon FAIcon={LegacyFAIcons.Cog} />
            </Link>
          )}
          <Link
            sx={navItemSxStyle}
            href={`/page-earlyInvolvement.html?emp=${employeeId}`}
          >
            <LegacyFAIcon FAIcon={LegacyFAIcons.ListAlt} />
          </Link>
          <LegacyNotificationsPaper>
            <Box sx={navItemSxStyle}>
              <LegacyFAIcon FAIcon={LegacyFAIcons.Bell} />
            </Box>
          </LegacyNotificationsPaper>
          <LegacyUserProfile
            renderProfileButton={(buttonComponent) => {
              return (
                <Link
                  sx={navItemSxStyle}
                  href={`/page-people.html?emp=${loggedInEmployeeId}`}
                >
                  {buttonComponent}
                </Link>
              );
            }}
          />
          <Link sx={navItemSxStyle} href={'/page-logout'}>
            <LegacyFAIcon FAIcon={LegacyFAIcons.SignOut} />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
